import { type StateKind } from '@/designs-dev/handover/types';

// A prototype "scene": one named, reproducible state of a prototype, paired
// with the `initial` props that drive the live prototype into it. The single
// source of truth shared by two surfaces — the live state select in the chrome
// above the frame (StateSelector) and the flows page's states — so the set of
// states a prototype can be in is defined once and can't drift between them.
//
// `kind` reuses the handover's StateKind so the select can group scenes the
// same way the handover page does (happy / empty / edge / error).
export type Scene<I> = {
  id: string;
  label: string;
  kind: StateKind;
  // How to reach it, mirroring StateCase.repro — optional, for the handover.
  repro?: string;
  /**
   * Which part of the journey this screen belongs to, for a prototype whose
   * screens are steps rather than states. Groups the live select by section
   * instead of by kind — see StateSelector's `sections`.
   */
  section?: string;
  // The props that put the prototype in this state.
  initial: I;
};

// Build an id → scene accessor for a catalog. Throws on an unknown id so a
// typo in a handover render or a stage default fails loudly rather than
// silently rendering the wrong state.
export const makeSceneLookup =
  <I>(scenes: Scene<I>[]) =>
  (id: string): Scene<I> => {
    const scene = scenes.find((s) => s.id === id);
    if (!scene) {
      throw new Error(`Unknown scene id: ${id}`);
    }
    return scene;
  };
